/**
 * smsGuidance — the ONE place that turns an SMS message into user-facing guidance
 * (a live size/cost indicator + a warn-or-block message + standardized tips).
 *
 * Every SMS-composing surface in the app (patient chat, Conversations, campaign
 * composer, workflow SMS actions, SMS templates, …) renders the same guidance by
 * going through here, so the wording, thresholds, and tips are identical everywhere
 * — no per-composer copies to drift. The reusable <SmsGuidance> component wraps this.
 *
 * Pure calculation lives in smsSegments.js (evaluateOutboundMessage, shared
 * byte-for-byte with the Lambda layer). This module only maps that result to copy,
 * so the numbers the customer sees match exactly what the backend bills + enforces.
 *
 * The tip copy is kept in sync with the customer docs ("Text costs and delivery" →
 * "Ways to use fewer credits") so the app and the Help Center say the same thing.
 */
import { evaluateOutboundMessage, substituteTags, CARRIER_MAX_SEGMENTS_PER_MESSAGE } from './smsSegments';

// A message this many segments or longer is "getting long/costly" → a non-blocking
// heads-up. (Over CARRIER_MAX_SEGMENTS_PER_MESSAGE it's a hard block instead.)
export const SEGMENTS_WARN_THRESHOLD = 4;

// TEMPLATE headroom: a template whose SAMPLE render lands within this many segments
// of the hard cap gets a distinct warning, because real patient data (a long name,
// practice details, an accented character) is substituted at send time and can push
// it OVER the cap for some recipients — where the backend then blocks it. Warn the
// author to leave room. (Applies only when substituteTags/vars is set — i.e. the text
// is a template with runtime variables; direct-typed messages don't expand later.)
export const HEADROOM_SEGMENTS = 2;

// Standardized, reusable tips — the single source of truth for this copy.
export const SMS_TIPS = {
  shorten: 'Shorten your message — texts over ~160 characters split into multiple credits, and each part adds to the cost.',
  specialChars: 'Remove emoji and special characters — a single emoji or symbol can more than double a text’s length.',
  linkInstead: 'For longer content, link to a page instead of putting it all in the text.',
};

/**
 * The SHORTEST text this body can possibly render to at send time: every {{merge_tag}} blanked,
 * and every link stand-in ([Form: …] / [Link: …] placeholders, HTML <a> anchors — the backend's
 * processLink swaps these for URLs) removed. Removing characters can never ADD segments, and
 * accented literal text stays UCS-2 whatever the variables hold — so if even this floor is over
 * the carrier cap, the real send is CERTAIN to be blocked. 2026-09-23 Allay incident: an 880-char
 * English+Spanish text with one {{patient_first_name}} was only WARNED (estimate-over), sent,
 * reported "sent", and blocked by the backend — the patient silently got nothing.
 */
export function minimumRenderedBody(body) {
  return String(body == null ? '' : body)
    .replace(/\{\{\s*[\w.]+\s*\}\}/g, '')
    .replace(/<a\b[^>]*>[\s\S]*?<\/a>/gi, '')
    .replace(/\[(?:Form|Link):[^\]]*\]/g, '');
}

function creditsLabel(n) {
  return `${n} credit${n === 1 ? '' : 's'}`;
}

// "1,724 characters" — the live size shown in the composer counter so the sender
// sees exactly where they stand (and, on a block, how much to remove).
function charsLabel(n) {
  return `${n.toLocaleString()} character${n === 1 ? '' : 's'}`;
}

/**
 * @param {string} body        the message text as typed (or a template with {{tags}})
 * @param {{media?:Array, smsFormatting?:'original'|'optimized',
 *          substituteTags?:boolean, vars?:Object}} [opts]
 *   - substituteTags/vars: TEMPLATE surfaces (SMS templates, template pickers) carry
 *     unresolved {{merge_tags}}. Measuring the literal template is wrong twice over:
 *     `{{patient_first_name}}` is ~20 chars but renders to ~10, AND the real data can
 *     carry special characters the template can't (a name like "José" flips the sent
 *     text to UCS-2 → double cost). Pass substituteTags:true (or real vars) to measure
 *     the RENDERED text with conservative sample values, exactly as the campaign
 *     composer does. Direct-chat surfaces omit this and measure the text as typed.
 *     Known limitation: sample names are ASCII, so an accented real name can still
 *     push a "safe-looking" template to UCS-2 for some patients — data-dependent.
 * @returns {{
 *   segments:number, billableCredits:number, creditsLabel:string,
 *   charCount:number, charsLabel:string, explainer:string, isEstimate:boolean,
 *   messageType:'sms'|'mms', encoding:string, wasNormalized:boolean,
 *   blocked:boolean, severity:'ok'|'warn'|'block',
 *   title:string, message:string, tips:string[]
 * }}
 */
export function getSmsGuidance(body, opts = {}) {
  const isTemplate = !!(opts.substituteTags || opts.vars);
  const measured = isTemplate
    ? substituteTags(body == null ? '' : String(body), opts.vars)
    : body;
  const r = evaluateOutboundMessage(measured, { media: opts.media, smsFormatting: opts.smsFormatting });
  const isMms = r.messageType === 'mms';
  // After auto-cleanup, a still-UCS-2 SMS means unreplaceable characters remain
  // (emoji, exotic symbols) — the biggest silent cost inflator.
  const hasSpecialChars = !isMms && r.encoding === 'UCS-2';
  // A template whose sample render is within HEADROOM_SEGMENTS of the cap could tip
  // OVER once real patient data is substituted at send time.
  const templateNearCap = isTemplate && !isMms && !r.blocked
    && r.segments >= (CARRIER_MAX_SEGMENTS_PER_MESSAGE - HEADROOM_SEGMENTS);

  // Live character count of the FINAL (billed) text — the number that actually maps
  // to the segments/credits shown, so "you're at N characters" stays consistent with
  // the credit count. Shown in the counter on every SMS surface.
  const charCount = (r.finalBody == null ? '' : String(r.finalBody)).length;

  // Whether the body contains merge variables ({{patient_first_name}}, …). If so, the real send
  // length depends on each patient's data, so the character/credit counts are an ESTIMATE — we
  // surface that (a "~" on the counter + a note in the tooltip).
  const hasMergeTags = /\{\{\s*[\w.]+\s*\}\}/.test(body == null ? '' : String(body));

  // Plain-language explainer for the counter's info (i) tooltip — the ONE place that teaches what a
  // credit is, why emojis/special characters cost more, and (with variables) that the count is an
  // estimate. No GSM-7/UCS-2 jargon; shown on every SMS surface via the shared component.
  const isUcs2 = r.encoding === 'UCS-2';
  let explainer;
  if (isMms) {
    explainer = 'A picture message is billed as a flat 3 credits, whatever its length.';
  } else if (isUcs2) {
    explainer = `This message uses emojis or special characters, which cost more — only about 70 characters fit per credit. It will use ${creditsLabel(r.billableCredits)} to send.`;
  } else {
    explainer = `Texts are billed in credits: about 160 characters fit in one credit. This message will use ${creditsLabel(r.billableCredits)} to send.`;
  }
  if (hasMergeTags) {
    explainer += ' This is an estimate — variables like {{patient_first_name}} are filled in with each patient’s details when the text is sent, so the real length can differ.';
  }

  // Over the carrier cap? HARD-BLOCK when we know the send can't fit: either the EXACT length (no
  // merge variables) is over, or — with variables — even the shortest possible render
  // (minimumRenderedBody) is over. Otherwise, with variables the real send length is unknown (the
  // backend renders, translates, and substitutes patient data), so an over-cap ESTIMATE only warns,
  // gets the user's acknowledgment, and the backend stays the true guard at send time. This single
  // centralized rule is inherited by every SMS surface (composers, sidebar, follow-up, and the wiki
  // save-gate).
  const overCap = r.blocked;
  const floor = (overCap && hasMergeTags)
    ? evaluateOutboundMessage(minimumRenderedBody(body), { media: opts.media, smsFormatting: opts.smsFormatting })
    : null;
  const floorOverCap = !!(floor && floor.blocked);
  const blocked = overCap && (!hasMergeTags || floorOverCap);

  let severity = 'ok';
  // `reason` names WHICH state fired so gating surfaces can act precisely — e.g. a template editor
  // PREVENTS save on 'block' but only asks for acknowledgment on 'estimate-over' / 'headroom'.
  // Values: 'ok' | 'block' | 'estimate-over' | 'headroom' | 'special' | 'long'.
  let reason = 'ok';
  let title = '';
  let message = '';
  const tips = [];

  if (blocked) {
    // Hard block: EXACT (no-variable) text this long — carriers reject it and you're billed for the
    // failed attempt, so it can never reach the patient as-is.
    severity = 'block';
    reason = 'block';
    // GSM-7 packs 153 chars per segment once concatenated, UCS-2 packs 67 (smsSegments
    // GSM_MULTI / UCS2_MULTI). The most a message can be and still send is the carrier
    // cap × that per-segment size — so this is roughly how many characters to cut.
    // With variables, count from the floor — "remove N" must be enough even for the shortest render.
    const basis = floorOverCap ? floor : r;
    const basisChars = floorOverCap ? String(floor.finalBody == null ? '' : floor.finalBody).length : charCount;
    const perSegmentChars = basis.encoding === 'UCS-2' ? 67 : 153;
    const maxSendableChars = CARRIER_MAX_SEGMENTS_PER_MESSAGE * perSegmentChars;
    const removeToSend = Math.max(1, basisChars - maxSendableChars);
    title = 'This message is too long to send';
    message = `Mobile carriers won’t reliably deliver a text this long — it splits into more than ${CARRIER_MAX_SEGMENTS_PER_MESSAGE} parts, and you'd still be charged for a message that doesn't arrive. You're at ${charsLabel(charCount)}; remove about ${removeToSend.toLocaleString()} to send.`;
    tips.push(SMS_TIPS.shorten);
    if (hasSpecialChars) tips.push(SMS_TIPS.specialChars);
    tips.push(SMS_TIPS.linkInstead);
  } else if (overCap) {
    // Estimate is over the cap, but the message has variables so the real length is unknown. Warn
    // and get acknowledgment instead of blocking — it may need shortening, or it may send and use
    // extra credits; the backend enforces the true carrier limit at send time.
    severity = 'warn';
    reason = 'estimate-over';
    title = 'This may go over the one-message limit';
    message = `This is an estimate — the real length depends on each patient's details and language. It may need shortening, or it may send and use more credits than usual. You'll be charged for the message that's actually sent.`;
    tips.push(SMS_TIPS.shorten);
    if (hasSpecialChars) tips.push(SMS_TIPS.specialChars);
    tips.push(SMS_TIPS.linkInstead);
  } else if (templateNearCap) {
    // Fits now with sample values, but real data is substituted at send time and can
    // push it over — where the backend blocks it (and a patient silently misses it).
    severity = 'warn';
    reason = 'headroom';
    title = 'This template is close to the limit';
    message = `With real patient data — a long name, practice details, or an accented character — this could go over the limit and fail to send for some patients. It uses ${creditsLabel(r.billableCredits)} per recipient right now; leave some room.`;
    tips.push(SMS_TIPS.shorten);
    if (hasSpecialChars) tips.push(SMS_TIPS.specialChars);
  } else if (hasSpecialChars) {
    // Deliverable, but an emoji/symbol is quietly inflating the cost.
    severity = 'warn';
    reason = 'special';
    title = 'This message costs more than it looks';
    message = `An emoji or special character is making this text use ${creditsLabel(r.billableCredits)} per recipient instead of fewer.`;
    tips.push(SMS_TIPS.specialChars);
    tips.push(SMS_TIPS.shorten);
  } else if (!isMms && r.segments >= SEGMENTS_WARN_THRESHOLD) {
    // Deliverable, but long enough to be worth trimming.
    severity = 'warn';
    reason = 'long';
    title = 'This is a long message';
    message = `This text uses ${creditsLabel(r.billableCredits)} per recipient. Trimming it lowers the cost on every send.`;
    tips.push(SMS_TIPS.shorten);
  }

  return {
    segments: r.segments,
    billableCredits: r.billableCredits,
    creditsLabel: creditsLabel(r.billableCredits),
    charCount,
    charsLabel: charsLabel(charCount),
    explainer,
    isEstimate: hasMergeTags,
    messageType: r.messageType,
    encoding: r.encoding,
    wasNormalized: r.wasNormalized,
    blocked,
    severity,
    reason,
    title,
    message,
    tips,
  };
}
